"use client";

import React, { useRef } from "react";
import { useScroll, useTransform, motion } from "framer-motion";

interface ContainerScrollProps {
  titleComponent: string | React.ReactNode;
  children: React.ReactNode;
}

export const ContainerScroll = ({
  titleComponent,
  children,
}: ContainerScrollProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  // Animation completes by the time element is centered (0.5)
  const ANIMATION_START = 0.15;
  const ANIMATION_END = 0.45;

  const scaleDimensions = useTransform(scrollYProgress, [ANIMATION_START, ANIMATION_END], [0.85, 1]);
  const rotate = useTransform(scrollYProgress, [ANIMATION_START, ANIMATION_END], [20, 0]);
  const translateY = useTransform(scrollYProgress, [ANIMATION_START, ANIMATION_END], [0, -80]);
  const opacity = useTransform(scrollYProgress, [ANIMATION_START, ANIMATION_START + 0.08], [0, 1]);

  return (
    <div
      className="flex items-center justify-center relative"
      ref={containerRef}
    >
      <div
        className="w-full relative"
        style={{ perspective: "1000px" }}
      >
        <Header translate={translateY} titleComponent={titleComponent} />
        <Card rotate={rotate} scale={scaleDimensions} opacity={opacity}>
          {children}
        </Card>
      </div>
    </div>
  );
};

const Header = ({
  translate,
  titleComponent,
}: {
  translate: ReturnType<typeof useTransform>;
  titleComponent: React.ReactNode;
}) => {
  return (
    <motion.div
      style={{ translateY: translate }}
      className="max-w-5xl mx-auto text-center mb-4 md:mb-8"
    >
      {titleComponent}
    </motion.div>
  );
};

const Card = ({
  rotate,
  scale,
  opacity,
  children,
}: {
  rotate: ReturnType<typeof useTransform>;
  scale: ReturnType<typeof useTransform>;
  opacity: ReturnType<typeof useTransform>;
  children: React.ReactNode;
}) => {
  return (
    <motion.div
      style={{
        rotateX: rotate,
        scale,
        opacity,
        boxShadow:
          "0 0 #0000004d, 0 9px 20px #0000004a, 0 37px 37px #00000042, 0 84px 50px #00000026, 0 149px 60px #0000000a, 0 233px 65px #00000003",
      }}
      className="max-w-5xl -mt-4 mx-auto h-[14rem] sm:h-[20rem] md:h-[40rem] w-full border-4 border-[#6C6C6C] p-2 md:p-6 bg-[#222222] rounded-[30px] shadow-2xl"
    >
      <div className="h-full w-full overflow-hidden rounded-2xl bg-gray-100">
        {children}
      </div>
    </motion.div>
  );
};
